import { useState } from 'react';
import { Download, ExternalLink, FileText, Link2, Loader2 } from 'lucide-react';
import { useWebinarT } from '@/hooks/useWebinarT';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import type { SharedResourceDownloadUrl, SharedResourcePublic } from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { getReadableTextColor } from '@/utils/colors';
import { fetchSharedResourceDownloadUrl } from '@/features/webinar/webinarThunks';
import { canPreviewInTab, resolveSharedResourceOpening } from '@/lib/sharedResourceOpening';
import { closePreviewTabIfBlank } from '@/lib/blankPreviewTab';

export default function SharedResourceViewer({
  webinarId,
  sessionIdentifier,
  authorization,
  visualIdentity,
  resources,
  requestDownloadUrl,
}: {
  webinarId: number;
  sessionIdentifier: string;
  authorization: string;
  visualIdentity?: WorkspaceVisualIdentity | null;
  resources?: SharedResourcePublic[];
  // Salle de test : URL signée par la route admin du run, sans état de session ni analytics.
  requestDownloadUrl?: (sharedResourceId: number) => Promise<SharedResourceDownloadUrl>;
}) {
  const { t } = useWebinarT('createWebinar');
  const dispatch = useAppDispatch();
  const liveSharedResources = useAppSelector((s: RootState) => s.webinar.currentSharedResources);
  const currentSharedResources = resources ?? liveSharedResources;
  const [loadingId, setLoadingId] = useState<number | null>(null);

  if (currentSharedResources.length === 0) return null;

  const isDark = visualIdentity?.theme === 'DARK';
  const buttonBg = visualIdentity?.contrastColor || '#2356CC';
  const buttonText = getReadableTextColor(buttonBg);

  const handleOpenLink = (url: string | null) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // L'URL de téléchargement est signée en pièce jointe : le clic ne quitte donc pas le live, et un
  // onglet resterait blanc. On ouvre un onglet seulement pour les fichiers que le back sait afficher.
  const startDownload = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener noreferrer';
    link.download = '';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDownload = async (resource: SharedResourcePublic) => {
    if (!requestDownloadUrl && (!webinarId || !sessionIdentifier || !authorization)) return;
    const sharedResourceId = resource.id;
    // Ouverture synchrone d'un onglet pour éviter le blocage popup après l'await, et seulement si
    // ce navigateur sait afficher le fichier : sinon il le télécharge et l'onglet resterait blanc.
    const previewWindow = canPreviewInTab(resource.mimeType, navigator.pdfViewerEnabled === true)
      ? window.open('about:blank', '_blank')
      : null;
    if (previewWindow) previewWindow.opener = null;
    setLoadingId(sharedResourceId);
    try {
      const result = requestDownloadUrl
        ? await requestDownloadUrl(sharedResourceId)
        : await dispatch(
            fetchSharedResourceDownloadUrl({
              webinarId,
              sessionIdentifier,
              sharedResourceId,
              authorization,
            }),
          ).unwrap();
      const opening = resolveSharedResourceOpening(result);
      if (!opening) {
        previewWindow?.close();
        toast.error(t('sharedResourcesViewer.downloadError'));
        return;
      }
      if (opening.mode === 'download' || !previewWindow) {
        previewWindow?.close();
        startDownload(result.downloadUrl);
        return;
      }
      previewWindow.location.href = opening.url;
      closePreviewTabIfBlank(previewWindow);
      startDownload(result.downloadUrl);
    } catch {
      previewWindow?.close();
      toast.error(t('sharedResourcesViewer.downloadError'));
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div
      className="w-full rounded-none border-0 lg:rounded-2xl lg:border p-4 flex flex-col gap-3"
      style={{
        backgroundColor: isDark ? '#1B1D2A' : '#FFFFFF',
        color: isDark ? '#FFFFFF' : '#000000',
        borderColor: isDark ? '#2E3142' : '#EAEAEC',
      }}
    >
      <p className="text-sm font-semibold">{t('sharedResourcesViewer.title')}</p>
      {currentSharedResources.map((resource) => {
        const isFile = resource.type === 'FILE';
        return (
          <div key={resource.id} className="flex items-start gap-3">
            <div className="flex-1 min-w-0 flex items-start gap-2">
              <div className="size-9 shrink-0 bg-medium rounded-lg flex items-center justify-center">
                {isFile ? (
                  <FileText className="size-4 text-black" />
                ) : (
                  <Link2 className="size-4 text-black" />
                )}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium line-clamp-2 wrap-break-word">
                  {resource.title}
                </span>
                {resource.description && (
                  <span className="text-xs opacity-70 line-clamp-2 wrap-break-word">
                    {resource.description}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium cursor-pointer disabled:cursor-not-allowed"
              style={{ backgroundColor: buttonBg, color: buttonText }}
              onClick={() =>
                isFile ? handleDownload(resource) : handleOpenLink(resource.externalUrl)
              }
              disabled={isFile && loadingId === resource.id}
            >
              {isFile ? (
                loadingId === resource.id ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Download className="size-4" />
                )
              ) : (
                <ExternalLink className="size-4" />
              )}
              {isFile ? t('sharedResourcesViewer.download') : t('sharedResourcesViewer.open')}
            </button>
          </div>
        );
      })}
    </div>
  );
}
